import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { communityList, type CommunityReply } from "@/mocks/community";
import Breadcrumb from "@/components/feature/Breadcrumb";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";

export default function CommunityDetail() {
  const { id } = useParams();
  const post = communityList.find((item) => item.id === id);

  const [replies, setReplies] = useState<CommunityReply[]>(post ? post.replyList : []);
  const [draft, setDraft] = useState("");
  const [hint, setHint] = useState<string | null>(null);

  useDocumentMeta(
    post ? `${post.title} - 社区讨论 - 色花堂` : "帖子不存在 - 色花堂",
    post?.content[0],
  );

  if (!post) {
    return (
      <div className="px-4 py-16 md:px-8">
        <div className="mx-auto w-full max-w-2xl">
          <EmptyState
            icon="ri-chat-off-line"
            title="没有找到这篇帖子"
            description="该帖子可能已被删除或链接有误，返回社区首页看看其他讨论吧。"
            actionLabel="返回社区首页"
            actionTo="/community"
          />
        </div>
      </div>
    );
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text) {
      setHint("回复内容不能为空");
      return;
    }
    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, "0");
    const time = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setReplies((prev) => [...prev, { user: "当前访客", content: text, time }]);
    setDraft("");
    setHint("回复已添加（仅供演示，不会真实提交到服务器）");
  };

  return (
    <article>
      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto w-full max-w-[900px] px-4 py-6 md:px-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "社区", to: "/community" },
              { label: post.title },
            ]}
          />
          <span className="mt-5 inline-block rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
            {post.tag}
          </span>
          <h1 className="mt-3 font-heading text-2xl font-extrabold leading-tight text-foreground-950 md:text-3xl">
            {post.title}
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line text-base" aria-hidden="true"></i>
              {post.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line text-base" aria-hidden="true"></i>
              {post.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-base" aria-hidden="true"></i>
              {post.views.toLocaleString()} 次浏览
            </span>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[900px] px-4 py-8 md:px-8">
        <div className="flex flex-col gap-5">
          {post.content.map((paragraph, idx) => (
            <p key={idx} className="text-base leading-loose text-foreground-800">
              {paragraph}
            </p>
          ))}
        </div>

        {post.images.map((image, idx) => (
          <figure key={idx} className="mt-6 overflow-hidden rounded-lg border border-background-200">
            <div className="h-56 w-full md:h-80">
              <img
                src={image}
                alt={`${post.title} 配图 ${idx + 1}`}
                title={`${post.title} - 色花堂社区`}
                className="h-full w-full object-cover object-top"
              />
            </div>
          </figure>
        ))}

        <div className="mt-8 flex flex-wrap items-center gap-2">
          <span className="text-sm text-foreground-500">话题标签：</span>
          <Link
            to="/community"
            className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200 cursor-pointer"
          >
            #{post.tag}
          </Link>
          <Link
            to="/community"
            className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200 cursor-pointer"
          >
            #社区讨论
          </Link>
        </div>

        <div className="mt-10">
          <h2 className="font-heading text-lg font-bold text-foreground-950">
            全部回复 <span className="text-primary-600">({replies.length})</span>
          </h2>

          <ul className="mt-4 flex flex-col gap-3">
            {replies.map((reply, idx) => (
              <li key={idx} className="flex gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className="ri-user-line text-lg" aria-hidden="true"></i>
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="text-sm font-semibold text-foreground-900">{reply.user}</span>
                    <span className="text-xs text-foreground-400">{reply.time}</span>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">{reply.content}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <label htmlFor="reply-content" className="font-heading text-sm font-bold text-foreground-900">
            发表回复
          </label>
          <textarea
            id="reply-content"
            name="reply-content"
            rows={4}
            maxLength={500}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              if (hint) setHint(null);
            }}
            placeholder="友善发言，理性讨论（最多 500 字）"
            className="mt-3 w-full resize-none rounded-md border border-background-300/70 bg-background-100 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
          ></textarea>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-foreground-400">{draft.length} / 500</span>
            <button
              type="submit"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-send-plane-fill text-base" aria-hidden="true"></i>
              提交回复
            </button>
          </div>
          {hint && <p className="mt-3 text-xs text-accent-700">{hint}</p>}
        </form>

        <div className="mt-8">
          <Link
            to="/community"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line text-base" aria-hidden="true"></i>
            返回社区首页
          </Link>
        </div>
      </div>
    </article>
  );
}